import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Grid, TextField, Typography, FormControl, InputLabel, Select, MenuItem, FormHelperText, CircularProgress, InputAdornment } from '@mui/material';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useDataAuth } from '../../hooks/useDataAuth';
import { useNotification } from '../../hooks/useNotification';
import { useDianAutocomplete } from '../../hooks/useDianAutocomplete';
import taxResponsibilities from "../../data/taxResponsibilities.json";
import identificationTypes from "../../data/identificationTypes.json";
import { MultipleSelect } from '../others/MultipleSelect';
import { fetchSupplierByNit } from '../../services/proveedor.service';
import { calculateDV } from '../../utils/invoice';
import '../../styles/styleInput/inputStyle.css';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';

export const LegalInformationForm = forwardRef(({ dto, handleInputChange, handleDirectInputChange }, ref) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [isNitDisabled, setIsNitDisabled] = useState(false);
    const [{ userSession }] = useDataAuth();
    const [sendNotification] = useNotification();

    useEffect(() => {
        if (location.state?.config?.data) {
            const { id } = location.state.config.data;
            setIsNitDisabled(Boolean(id));
        }
    }, [location.state]);

    // ─── autocomplete los campos del proveedor
    const { loadingDian, fetchDianInfo, handleNitChange } = useDianAutocomplete({
        token: userSession?.token,
        dto,
        handleDirectInputChange,
        sendNotification,
        fieldMap: {
            business_name:          'businessName',
            supplier_tax_scheme:    'taxScheme',
            supplier_tax_level_code:'taxLevelCode',
            code_ciiu:              'ciiuCode',
            description_ciiu:       'ciiuDescription',
            type_ciiu:              'ciiuType',
        },
        checkDuplicate: isNitDisabled ? null : (nit) => fetchSupplierByNit(userSession.token, nit),
        duplicateMsg:   t('supplier.alreadyExists', 'Este proveedor ya existe en el sistema.'),
    });

    const handleSelectObligation = (event) => {
        const { target: { value } } = event;
        const formattedValue = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',') : []);
        handleDirectInputChange('supplier_tax_level_code', formattedValue);
    };

    const validateField = (name, value) => {
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };

    useImperativeHandle(ref, () => ({ validateForm }));

    const requiredField = ['type_identification', 'business_name', 'society_type', 'company_type', 'supplier_tax_scheme', 'supplier_tax_level_code'];
    const validateForm = () => {
        let hasError = false;
        requiredField.forEach((field) => {
            if (!validateField(field, dto[field])) hasError = true;
        });
        return !hasError;
    };

    const handleBlurNit = () => {
        if (dto.identification) {
            handleDirectInputChange('identification_digit', calculateDV(dto.identification));
        }
        fetchDianInfo();
    };

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="h6">{t("suppliers.legalInformation")}</Typography>
            </Grid>

            {/* NIT */}
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth
                    label={t("commonFields.nit")}
                    size="small"
                    name='identification'
                    onChange={handleNitChange}
                    onBlur={handleBlurNit}
                    value={dto.identification}
                    required
                    disabled={isNitDisabled}
                    helperText={loadingDian ? 'Consultando DIAN...' : ''}
                    slotProps={{
                        input: {
                            endAdornment: loadingDian ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={18} color="secondary" />
                                </InputAdornment>
                            ) : null,
                        }
                    }}
                />
            </Grid>

            {/* Dígito de Verificación */}
            <Grid size={{ xs: 12, sm: 4 }}>
                <TextField
                    fullWidth
                    name='identification_digit'
                    label={t("commonFields.verificationDigit")}
                    size="small"
                    value={dto.identification_digit}
                    onChange={handleInputChange}
                />
            </Grid>

            {/* Tipo de Identificación */}
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl
                    fullWidth size="small"
                    error={Boolean(errorInputs.type_identification?.isError)}
                    required
                >
                    <InputLabel>{t("commonFields.identificationType")}</InputLabel>
                    <Select
                        label={t("commonFields.identificationType")}
                        name="type_identification"
                        value={dto.type_identification}
                        onChange={(e) => {
                            handleInputChange(e);
                            fetchDianInfo({ type_identification: e.target.value });
                        }}
                    >
                        {identificationTypes.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                    {errorInputs.type_identification?.isError && (
                        <FormHelperText>{errorInputs.type_identification.messageError}</FormHelperText>
                    )}
                </FormControl>
            </Grid>

            {/* Razón Social — autocompletado por DIAN */}
            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    fullWidth size="small"
                    name='business_name'
                    label={t("commonFields.businessName")}
                    value={dto.business_name}
                    onChange={handleInputChange}
                    error={Boolean(errorInputs.business_name?.isError)}
                    helperText={errorInputs.business_name?.messageError || ''}
                    required
                />
            </Grid>

            {/* Tipo de Sociedad */}
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size='small' error={Boolean(errorInputs.society_type?.isError)} required>
                    <InputLabel>{t("commonFields.societyType")}</InputLabel>
                    <Select label={t("commonFields.societyType")} name='society_type' value={dto.society_type} onChange={handleInputChange}>
                        <MenuItem value="S.A.S">S.A.S</MenuItem>
                        <MenuItem value="Limitada">Limitada</MenuItem>
                        <MenuItem value="Anónima">Anónima</MenuItem>
                        <MenuItem value="Otra">Otra</MenuItem>
                    </Select>
                    {errorInputs.society_type?.isError && <FormHelperText>{errorInputs.society_type.messageError}</FormHelperText>}
                </FormControl>
            </Grid>

            {/* Tipo de Empresa */}
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size='small' error={Boolean(errorInputs.company_type?.isError)} required>
                    <InputLabel>{t("commonFields.companyType")}</InputLabel>
                    <Select label={t("commonFields.companyType")} name='company_type' value={dto.company_type} onChange={handleInputChange}>
                        <MenuItem value="Privada">Privada</MenuItem>
                        <MenuItem value="Pública">Pública</MenuItem>
                        <MenuItem value="Mixta">Mixta</MenuItem>
                        <MenuItem value="Sin ánimo de lucro">Sin ánimo de lucro</MenuItem>
                    </Select>
                    {errorInputs.company_type?.isError && <FormHelperText>{errorInputs.company_type.messageError}</FormHelperText>}
                </FormControl>
            </Grid>

            {/* Tipo de Tributo — autocompletado por DIAN */}
            <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth size="small" error={!!errorInputs.supplier_tax_scheme?.isError} required>
                    <InputLabel>{t("commonFields.taxType")}</InputLabel>
                    <Select label={t("commonFields.taxType")} name="supplier_tax_scheme" value={dto.supplier_tax_scheme} onChange={handleInputChange}>
                        <MenuItem value="01">IVA</MenuItem>
                        <MenuItem value="04">INC</MenuItem>
                        <MenuItem value="ZA">IVA e INC</MenuItem>
                        <MenuItem value="ZZ">No aplica</MenuItem>
                    </Select>
                    {errorInputs.supplier_tax_scheme?.isError && <FormHelperText>{errorInputs.supplier_tax_scheme.messageError}</FormHelperText>}
                </FormControl>
            </Grid>

            {/* Obligaciones — autocompletado por DIAN */}
            <Grid size={{ xs: 12, md: 4 }}>
                <MultipleSelect
                    data={taxResponsibilities}
                    values={Array.isArray(dto.supplier_tax_level_code) ? dto.supplier_tax_level_code : []}
                    handleChange={handleSelectObligation}
                    label={t("commonFields.obligations")}
                />
            </Grid>
        </Grid>
    );
});
